import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Alert,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  DateRange as DateRangeIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Close as ClearIcon
} from '@mui/icons-material';
import {
  format, addMonths, subMonths, startOfMonth, endOfMonth,
  startOfWeek, endOfWeek, addDays, isSameMonth, isSameDay,
  isAfter, isBefore, isToday, startOfDay, parseISO
} from 'date-fns';
import { fr as frLocale, enUS, es as esLocale, ar as arLocale } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import './AvailabilityFilter.css';

/**
 * 📅 FILTRE DE DISPONIBILITÉ — CALENDRIER INLINE AVEC RANGE
 *
 * Fonctionnalités :
 * ✅ Calendrier inline mois par mois avec navigation
 * ✅ Sélection de plage en deux clics (début → fin)
 * ✅ Highlight visuel de la plage sélectionnée
 * ✅ Raccourcis rapides (Aujourd'hui, 3 jours, 1 semaine, 1 mois)
 * ✅ Validation + internationalisation complète
 */
const AvailabilityFilter = forwardRef(({
  onAvailabilityChange,
  selectedAvailability,
  isExpanded,
  onToggleExpand,
  onNotification,
  externalFilters
}, ref) => {
  const { t, i18n } = useTranslation();

  // ── Locale date-fns ──────────────────────────────────────────────────────
  const dateLocale = i18n?.language?.startsWith('fr') ? frLocale
    : i18n?.language?.startsWith('ar') ? arLocale
    : i18n?.language?.startsWith('es') ? esLocale
    : enUS;

  // ── États ────────────────────────────────────────────────────────────────
  const [currentMonth, setCurrentMonth]     = useState(startOfMonth(new Date()));
  const [startDate,    setStartDate   ]     = useState(null);
  const [endDate,      setEndDate     ]     = useState(null);
  const [hovered,      setHovered     ]     = useState(null);
  const [dateError,    setDateError   ]     = useState(null);
  const [selectingEnd, setSelectingEnd]     = useState(false); // true = en attente du 2e clic

  const onChangeRef = useRef(onAvailabilityChange);
  useEffect(() => { onChangeRef.current = onAvailabilityChange; }, [onAvailabilityChange]);

  // ── Helpers ──────────────────────────────────────────────────────────────
  const today = startOfDay(new Date());

  const notify = useCallback((sd, ed) => {
    onChangeRef.current?.({
      startDate: sd ? format(sd, 'yyyy-MM-dd') : null,
      endDate:   ed ? format(ed, 'yyyy-MM-dd') : null,
      _activeCount: (sd ? 1 : 0) + (ed ? 1 : 0)
    });
  }, []);

  const activeFiltersCount = (startDate ? 1 : 0) + (endDate ? 1 : 0);

  // ── Sync external filters ────────────────────────────────────────────────
  useEffect(() => {
    if (!externalFilters) return;
    if (externalFilters.startDate) setStartDate(parseISO(externalFilters.startDate));
    if (externalFilters.endDate)   setEndDate(parseISO(externalFilters.endDate));
  }, [externalFilters]);

  // ── Reset si le parent envoie null ───────────────────────────────────────
  useEffect(() => {
    if (selectedAvailability === null) {
      setStartDate(null);
      setEndDate(null);
      setDateError(null);
      setSelectingEnd(false);
    }
  }, [selectedAvailability]);

  // ── Validation ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (startDate && endDate && isAfter(startDate, endDate)) {
      setDateError(t('availability.errors.startAfterEnd', 'La date de début doit être avant la date de fin'));
    } else {
      setDateError(null);
    }
  }, [startDate, endDate, t]);

  // ── Ref exposé au parent ─────────────────────────────────────────────────
  const getCurrentData = useCallback(() => ({
    startDate: startDate ? format(startDate, 'yyyy-MM-dd') : null,
    endDate:   endDate   ? format(endDate,   'yyyy-MM-dd') : null,
    _activeCount: (startDate ? 1 : 0) + (endDate ? 1 : 0)
  }), [startDate, endDate]);

  useImperativeHandle(ref, () => ({ getCurrentData }), [getCurrentData]);

  // ── Clic sur un jour ─────────────────────────────────────────────────────
  const handleDayClick = (day) => {
    if (isBefore(day, today)) return; // passé ignoré

    if (!selectingEnd) {
      // 1er clic → date de début
      setStartDate(day);
      setEndDate(null);
      setSelectingEnd(true);
      notify(day, null);
    } else {
      // 2e clic → date de fin
      if (isBefore(day, startDate)) {
        // Clic avant le début → nouveau départ
        setStartDate(day);
        setEndDate(null);
        notify(day, null);
      } else if (isSameDay(day, startDate)) {
        // ✅ Même jour → confirmer une journée unique (ex : recherche créneaux du 04/03)
        setEndDate(day);
        setSelectingEnd(false);
        notify(startDate, day);
      } else {
        // Clic après le début → plage normale
        setEndDate(day);
        setSelectingEnd(false);
        notify(startDate, day);
      }
    }
  };

  // ── Clear ────────────────────────────────────────────────────────────────
  const handleClear = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectingEnd(false);
    setDateError(null);
    notify(null, null);
  };

  // ── Raccourcis ───────────────────────────────────────────────────────────
  const applyPreset = (days) => {
    const sd = today;
    const ed = addDays(today, days);
    setStartDate(sd);
    setEndDate(ed);
    setSelectingEnd(false);
    notify(sd, ed);
  };

  // ── Génération des cellules du mois ──────────────────────────────────────
  const buildCalendarDays = () => {
    const start = startOfWeek(startOfMonth(currentMonth), { locale: dateLocale, weekStartsOn: 1 });
    const end   = endOfWeek(endOfMonth(currentMonth),     { locale: dateLocale, weekStartsOn: 1 });
    const days  = [];
    let cur = start;
    while (!isAfter(cur, end)) { days.push(cur); cur = addDays(cur, 1); }
    return days;
  };

  const calendarDays = buildCalendarDays();

  // ── Statut d'un jour pour le style ──────────────────────────────────────
  const isSingleDaySelection = !!(startDate && endDate && isSameDay(startDate, endDate));

  const getDayStatus = (day) => {
    const effectiveEnd = endDate || (selectingEnd && hovered && !isBefore(hovered, startDate) ? hovered : null);
    const isStart  = !!(startDate && isSameDay(day, startDate));
    const isEnd    = !!(effectiveEnd && isSameDay(day, effectiveEnd));
    const inRange  = !!(startDate && effectiveEnd
      && isAfter(day, startDate) && isBefore(day, effectiveEnd));
    const isPast   = isBefore(day, today);
    const isOther  = !isSameMonth(day, currentMonth);
    const isTodayDay = isToday(day);
    // Journée unique : start == end sur ce jour précis
    const isSingle = isSingleDaySelection && isStart;
    return { isStart, isEnd, inRange, isPast, isOther, isTodayDay, isSingle };
  };

  // ── Noms des jours de la semaine (L..D) ──────────────────────────────────
  const weekdayKeys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
  const weekdayFallbacks = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

  // ── Rendu ────────────────────────────────────────────────────────────────
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <Paper sx={{ borderRadius: 0, overflow: 'hidden', boxShadow: 'var(--filter-shadow-light)' }}>
        <Accordion
          expanded={isExpanded}
          onChange={() => onToggleExpand?.()}
          sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
        >
          {/* ── Header ── */}
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className="af-accordion-header"
            sx={{ '& .MuiAccordionSummary-expandIconWrapper': { color: '#6200ee' } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <DateRangeIcon sx={{ mr: 2, color: '#6200ee' }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#4b00b5' }}>
                  {t('availability.title', 'Disponibilité')}
                </Typography>
                <Typography variant="caption" sx={{ color: '#6200ee', opacity: 0.8 }}>
                  {t('availability.subtitle', 'Filtrer par dates')}
                </Typography>
              </Box>
              <AnimatePresence>
                {activeFiltersCount > 0 && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0 }}
                  >
                    <Chip
                      size="small"
                      label={activeFiltersCount}
                      className="af-badge"
                      sx={{ minWidth: 22, height: 22 }}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </Box>
          </AccordionSummary>

          <AccordionDetails sx={{ p: 2 }}>
            <Stack spacing={2}>

              {/* ── Erreur ── */}
              <AnimatePresence>
                {dateError && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <Alert severity="error" variant="outlined">{dateError}</Alert>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* ── Raccourcis ── */}
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                  {t('availability.quickPresets', 'Raccourcis :')}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {[
                    { label: t('availability.preset.1day',  "Aujourd'hui"), days: 0 },
                    { label: t('availability.preset.3days', '3 jours'),     days: 3 },
                    { label: t('availability.preset.week',  '1 semaine'),   days: 7 },
                    { label: t('availability.preset.month', '1 mois'),      days: 30 }
                  ].map(({ label, days }) => (
                    <Chip
                      key={days}
                      label={label}
                      size="small"
                      variant={
                        startDate && endDate &&
                        isSameDay(startDate, today) &&
                        isSameDay(endDate, addDays(today, days))
                          ? 'filled' : 'outlined'
                      }
                      onClick={() => applyPreset(days)}
                      sx={{
                        cursor: 'pointer', fontSize: '0.7rem',
                        '&:hover': { bgcolor: '#f3e8ff', borderColor: '#6200ee' }
                      }}
                    />
                  ))}
                </Box>
              </Box>

              {/* ── Calendrier inline ── */}
              <Box className="af-calendar" sx={{ userSelect: 'none' }}>
                {/* Navigation mois */}
                <Box className="af-calendar-header">
                  <IconButton size="small" className="af-nav-button"
                    onClick={() => setCurrentMonth(m => subMonths(m, 1))}>
                    <ChevronLeftIcon fontSize="small" />
                  </IconButton>
                  <span className="af-calendar-title">
                    {format(currentMonth, 'MMMM yyyy', { locale: dateLocale })}
                  </span>
                  <IconButton size="small" className="af-nav-button"
                    onClick={() => setCurrentMonth(m => addMonths(m, 1))}>
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Instruction contextuelle */}
                <div className="af-calendar-instructions">
                  <p>
                    {!startDate || !selectingEnd
                      ? t('availability.selectStartDate', 'Cliquez sur une date de début')
                      : hovered && isSameDay(hovered, startDate)
                        ? t('availability.confirmSingleDay', 'Recliquez pour une journée unique')
                        : t('availability.selectEndDate',   'Cliquez sur une date de fin')}
                  </p>
                </div>

                {/* Jours de la semaine */}
                <div className="af-weekdays">
                  {weekdayKeys.map((key, i) => (
                    <div key={key} className="af-weekday">
                      {t(`availability.weekdays.${key}`, weekdayFallbacks[i])}
                    </div>
                  ))}
                </div>

                {/* Grille des jours */}
                <div className="af-calendar-grid">
                  {calendarDays.map((day, idx) => {
                    const { isStart, isEnd, inRange, isPast, isOther, isTodayDay, isSingle } = getDayStatus(day);
                    const clickable = !isPast && !isOther;

                    const dayClass = [
                      'af-day',
                      isSingle                              ? 'af-day--single' : '',
                      isStart && !isSingle                  ? 'af-day--start'  : '',
                      isEnd   && !isSingle                  ? 'af-day--end'    : '',
                      inRange                               ? 'af-day--range'  : '',
                      isTodayDay && !isStart && !isEnd       ? 'af-day--today'  : '',
                      isPast                                ? 'af-day--past'   : '',
                      isOther                               ? 'af-day--other'  : ''
                    ].filter(Boolean).join(' ');

                    return (
                      <div
                        key={idx}
                        className={dayClass}
                        onClick={() => clickable && handleDayClick(day)}
                        onMouseEnter={() => selectingEnd && !isPast && setHovered(day)}
                        onMouseLeave={() => setHovered(null)}
                      >
                        <span className="af-day-number">{format(day, 'd')}</span>
                      </div>
                    );
                  })}
                </div>
              </Box>

              {/* ── Résumé de la sélection ── */}
              <AnimatePresence>
                {(startDate || endDate) && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 6 }}
                  >
                    <div className="af-selection-summary">
                      <div>
                        <span className="af-selection-summary-label">
                          {isSingleDaySelection
                            ? t('availability.singleDay', 'Journée sélectionnée')
                            : t('availability.period', 'Période sélectionnée')}
                        </span>
                        <span className="af-selection-summary-dates">
                          {isSingleDaySelection
                            ? (startDate ? format(startDate, 'dd/MM/yyyy') : '–')
                            : <>{startDate ? format(startDate, 'dd/MM/yyyy') : '–'}
                                {' '}{t('availability.to', 'au')}{' '}
                                {endDate ? format(endDate, 'dd/MM/yyyy') : '–'}</>
                          }
                        </span>
                      </div>
                      <Tooltip title={t('availability.clear', 'Effacer')}>
                        <IconButton size="small" className="af-clear-btn" onClick={handleClear}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

            </Stack>
          </AccordionDetails>
        </Accordion>
      </Paper>
    </motion.div>
  );
});

export default AvailabilityFilter;
